<template>
  <Journal :date="date" :value="value">
    <!-- Render Entry when:
         - value exists (normal case), OR
         - isLoading is true (loading new date, show skeleton)
         Don't render when value is undefined AND isLoading is false (navigating away).
         This prevents useDocumentMerge from seeing skeletonEntry as a "remote change"
         and trying to merge against stale base during route transitions. -->
    <Entry
      v-if="value || isLoading"
      ref="component"
      :key="entryKey"
      :value="value ?? skeletonEntry"
      :is-loading="isLoading"
    />
  </Journal>
</template>

<script lang="ts">
import { startOfToday } from "date-fns";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  onUpdated,
} from "vue";
import { useRoute } from "vue-router";

import { getDateFromIso } from "@/components/dates";
import * as inject from "@/components/inject";
import Entry from "@/components/journal/Entry.vue";
import Journal from "@/components/journal/Journal.vue";
import {
  useJournalEntry,
  type EntryRecord,
} from "@/composables/useJournalEntry";

import { useObject } from "./object";

export type { EntryRecord } from "@/composables/useJournalEntry";

export default defineComponent({
  components: {
    Journal,
    Entry,
  },
  props: {
    // Organization scope, set by the org-scoped route. The journal is
    // personal-workspace only today; reserved for per-org journal support.
    orgId: { type: String, required: false },
  },
  async setup() {
    const controller = inject.controller();
    const route = useRoute();
    const profile = inject.profile();
    const logger = inject.logger();

    // Get date from query param
    const date = computed(() =>
      route.query?.["date"] && typeof route.query["date"] === "string"
        ? getDateFromIso(route.query["date"])
        : startOfToday()
    );

    const journal = useJournalEntry(controller, profile, date, logger);
    onBeforeUnmount(() => journal.release());

    // Provide database early (before any await) so child components can access it
    inject.database(journal.databaseForInject);

    const {
      initializeComponentFocus,
      installRouteCallbacks,
      release: releaseObject,
      setObjectValue,
      ...object
    } = useObject<EntryRecord>(route, journal.watchEntryNotes);
    installRouteCallbacks();
    onBeforeUnmount(releaseObject);
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);

    // Wait for the live query subscription to be established
    await journal.ready;

    // Connect the loaded entry to useObject's value management
    setObjectValue(journal.entry);

    return {
      date,
      entryKey: journal.entryKey,
      isLoading: journal.isLoading,
      skeletonEntry: journal.skeletonEntry,
      ...object,
    };
  },
});
</script>
